import type {
  ApiMonitorDefinition,
  ApiUrlPattern,
  MonitoringMetricDefinition,
  MonitoringPrimitive,
  PodMonitoringSchema,
} from "./types";

// Shared helpers for matching requests and shaping metric payloads.
// Converts arbitrary attribute values into primitives that are safe to emit.
export function normalizeAttributes(
  input?: Record<string, unknown>
): Record<string, MonitoringPrimitive> | undefined {
  if (!input) {
    return undefined;
  }

  const normalizedEntries: Array<[string, MonitoringPrimitive]> = [];

  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) {
      continue;
    }

    if (value === null) {
      normalizedEntries.push([key, null]);
      continue;
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      normalizedEntries.push([key, value]);
      continue;
    }

    if (value instanceof Error) {
      normalizedEntries.push([key, value.message]);
      continue;
    }

    try {
      normalizedEntries.push([key, JSON.stringify(value)]);
    } catch {
      normalizedEntries.push([key, String(value)]);
    }
  }

  if (normalizedEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(normalizedEntries);
}

// Pulls out the primary threshold so it can be attached to each emitted sample.
export function getThresholdSummary(
  metric: MonitoringMetricDefinition
): Record<string, MonitoringPrimitive> {
  const primaryThreshold = metric.thresholds[0];

  if (!primaryThreshold) {
    return {};
  }

  return {
    threshold_value: primaryThreshold.value,
    threshold_unit: primaryThreshold.unit,
    threshold_operator: primaryThreshold.operator,
    threshold_window_minutes: primaryThreshold.windowMinutes ?? null,
    threshold_percentile: primaryThreshold.percentile ?? null,
  };
}

// Checks whether a request URL matches one of the supported monitor patterns.
export function matchesUrlPattern(url: string, pattern: ApiUrlPattern): boolean {
  if (typeof pattern === "string") {
    return url.includes(pattern);
  }

  if ("all" in pattern) {
    return pattern.all.every((segment) => url.includes(segment));
  }

  pattern.lastIndex = 0;
  return pattern.test(url);
}

// Finds the API monitors whose method and URL rules match the current request.
export function resolveMatchingApiMonitors(
  schema: PodMonitoringSchema,
  url: string,
  method: string
): ApiMonitorDefinition[] {
  return (schema.apiMonitors ?? []).filter((monitor) => {
    const matchesMethod =
      !monitor.methods || monitor.methods.length === 0
        ? true
        : monitor.methods.some((allowedMethod) => allowedMethod.toUpperCase() === method);

    if (!matchesMethod) {
      return false;
    }

    return monitor.urlPatterns.some((pattern) => matchesUrlPattern(url, pattern));
  });
}
